import React, { type ComponentType, type ReactNode } from 'react'
import parse, { domToReact, Element, type DOMNode } from 'html-react-parser'

type HTMLRendererChildren = ReactNode & { trim: () => string }

interface HTMLRendererComponentProps {
  [attribute: string]: unknown
  children?: HTMLRendererChildren
  name?: string
}

type HTMLRendererComponent = ComponentType<HTMLRendererComponentProps>
type HTMLRendererComponentOverride = (component: HTMLRendererComponent) => HTMLRendererComponent

interface Props {
  componentOverrides?: Partial<Record<string, HTMLRendererComponentOverride>>
  components?: Partial<Record<string, HTMLRendererComponent>>
  html?: string
}

const defaultComponents = {}
const defaultComponentOverrides = {}

function getFallbackComponent(name: string): HTMLRendererComponent {
  return function HTMLRendererFallbackComponent(props) {
    return React.createElement(name, props)
  }
}

function resolveComponents(
  components: Partial<Record<string, HTMLRendererComponent>>,
  componentOverrides: Partial<Record<string, HTMLRendererComponentOverride>>,
) {
  return Object.entries(componentOverrides).reduce<Partial<Record<string, HTMLRendererComponent>>>((acc, [name, override]) => {
    if (override) {
      acc[name] = override(components[name] ?? getFallbackComponent(name))
    }

    return acc
  }, { ...components })
}

function HTMLRenderer(props: Props) {
  const {
    componentOverrides = defaultComponentOverrides,
    components = defaultComponents,
    html = '',
  } = props
  const resolvedComponents = resolveComponents(components, componentOverrides)
  const parserOptions = {
    replace(domNode: DOMNode) {
      if (!(domNode instanceof Element)) {
        return
      }

      const Component = resolvedComponents[domNode.name]
      if (!Component) {
        return
      }

      const children: ReactNode = domNode.children.length ? domToReact(domNode.children as Array<DOMNode>, parserOptions) : undefined
      const componentChildren = children as HTMLRendererChildren | undefined

      return <Component name={domNode.name} {...domNode.attribs}>{componentChildren}</Component>
    },
  }

  return <>{parse(html, parserOptions)}</>
}

export default HTMLRenderer
